// Interface vocale mains libres — Web Speech API (reconnaissance) +
// speechSynthesis (lecture). Pensée pour le technicien au microscope :
// « carte A2337 », « symptôme pas de charge », « diagnostic », « suivant »…
//
// Choix v1 : Web Speech API = zéro poids dans le bundle, FR natif sur les
// Android/Chrome du terrain. Selon le navigateur, la reconnaissance peut
// passer par le réseau (la synthèse vocale, elle, est toujours locale).
// Si l'API est absente (vieux WebView), le bouton micro n'apparaît pas.

import type { BoardModel, DiagnoseResult } from "../domain/types";
import { toast } from "../ui/dom";

// ── Types minimaux Web Speech (absents de lib.dom selon la cible) ─────────
interface SRAlternative { transcript: string }
interface SRResultList { length: number; [i: number]: { 0: SRAlternative; isFinal: boolean } }
interface SREvent { results: SRResultList; resultIndex: number }
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: SREvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  start(): void;
  stop(): void;
}

function getSR(): (new () => SpeechRecognitionLike) | null {
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as (new () => SpeechRecognitionLike) | null;
}

export function voiceSupported(): boolean {
  return getSR() !== null;
}

// ── Synthèse vocale ────────────────────────────────────────────────────────
let lastSpoken = "";

export function speak(text: string, opts?: { interrupt?: boolean }): void {
  if (!("speechSynthesis" in window)) return;
  if (opts?.interrupt !== false) window.speechSynthesis.cancel();
  lastSpoken = text;
  const u = new SpeechSynthesisUtterance(text);
  u.lang = "fr-FR";
  u.rate = 1.0;
  window.speechSynthesis.speak(u);
}

function stopSpeaking(): void {
  if ("speechSynthesis" in window) window.speechSynthesis.cancel();
}

// ── Contexte fourni par la vue Diagnostic ─────────────────────────────────
export interface VoiceContext {
  boards: () => BoardModel[];
  selectBoard: (id: string) => void;       // met à jour le <select> + chips
  setSymptom: (text: string) => void;      // remplit le textarea
  runDiagnostic: () => Promise<void>;      // déclenche run()
  lastResult: () => DiagnoseResult | null; // dernier résultat affiché
}

let ctx: VoiceContext | null = null;
let rec: SpeechRecognitionLike | null = null;
let active = false;
let stepIndex = -1; // position dans la procédure lue

// Normalisation : minuscules + accents retirés, pour un matching robuste.
function norm(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
}

// ── Commandes ──────────────────────────────────────────────────────────────
function matchBoard(query: string): BoardModel | null {
  const q = norm(query).replace(/\s+/g, "");
  if (!q || !ctx) return null;
  let best: BoardModel | null = null;
  let bestScore = 0;
  for (const b of ctx.boards()) {
    const hay = norm(`${b.id} ${b.brand} ${b.model}`).replace(/\s+/g, "");
    // score = plus longue sous-chaîne de la requête présente dans la carte
    let score = 0;
    for (let len = q.length; len >= 3; len--) {
      for (let i = 0; i + len <= q.length; i++) {
        if (hay.includes(q.slice(i, i + len))) { score = len; break; }
      }
      if (score) break;
    }
    if (score > bestScore) { bestScore = score; best = b; }
  }
  return bestScore >= 3 ? best : null;
}

function readResults(): void {
  const res = ctx?.lastResult();
  if (!res) return speak("Aucun résultat. Dites : diagnostic.");
  if (res.causes.length === 0) return speak("Aucune panne connue ne correspond. Le cas est enregistré.");
  const top = res.causes.slice(0, 3).map((c, i) =>
    `${i + 1}. ${c.cause}, ${Math.round(c.weight * 100)} pour cent.`);
  speak(`Causes probables : ${top.join(" ")} Dites : suivant, pour la procédure.`);
  stepIndex = -1;
}

function readNextStep(): void {
  const res = ctx?.lastResult();
  const card = res?.symptom_card;
  const board = res?.board;
  if (!res || !card || !board) return speak("Pas de procédure disponible pour ce diagnostic.");
  stepIndex += 1;
  if (stepIndex >= card.checklist.length) {
    stepIndex = -1;
    return speak("Fin de la procédure.");
  }
  const step = card.checklist[stepIndex];
  const mp = board.measure_points.find((m) => m.label === step);
  const detail = mp ? `. Attendu : ${mp.expected}${mp.location ? `. Emplacement : ${mp.location}` : ""}` : "";
  speak(`Étape ${stepIndex + 1} sur ${card.checklist.length} : ${step}${detail}`);
}

async function handleCommand(raw: string): Promise<void> {
  const t = norm(raw);
  if (!t || !ctx) return;

  if (/^(stop|silence|tais toi)\b/.test(t)) return stopSpeaking();
  if (/^(repete|repeter|encore)\b/.test(t)) return speak(lastSpoken || "Rien à répéter.");
  if (/^aide\b/.test(t)) {
    return speak("Commandes : carte, suivi du modèle. Symptôme, suivi de la description. " +
      "Diagnostic. Lis les résultats. Suivant, pour la procédure. Répète. Stop.");
  }

  const mCard = t.match(/^carte\s+(.+)$/);
  if (mCard) {
    const b = matchBoard(mCard[1]);
    if (!b) return speak("Carte non trouvée. Répétez le numéro ou le modèle.");
    ctx.selectBoard(b.id);
    return speak(b.locked ? `${b.model} est verrouillée. Licence requise.` : `Carte ${b.model} sélectionnée.`);
  }

  const mSymp = t.match(/^sympt?ome\s+(.+)$/);
  if (mSymp) {
    ctx.setSymptom(mSymp[1]);
    return speak("Symptôme noté. Dites : diagnostic.");
  }

  if (/^(diagnostic|diagnostique|lance|analyse)\b/.test(t)) {
    speak("Analyse en cours.");
    // La lecture des résultats est déclenchée par announceResults() au rendu.
    return void ctx.runDiagnostic();
  }

  if (/^(lis|lire|lecture|resultats?)\b/.test(t)) return readResults();
  if (/^(suivant|etape suivante|prochaine etape|continue)\b/.test(t)) return readNextStep();

  // Pas une commande : si c'est une phrase, on la prend comme symptôme.
  if (t.length >= 8) {
    ctx.setSymptom(raw.trim());
    return speak("Symptôme noté. Dites : diagnostic.");
  }
  speak("Pas compris. Dites : aide, pour la liste des commandes.");
}

// ── Cycle écoute mains libres ──────────────────────────────────────────────
function startRecognition(onState: (listening: boolean) => void): void {
  const SR = getSR();
  if (!SR) return;
  rec = new SR();
  rec.lang = "fr-FR";
  rec.continuous = false;     // une phrase à la fois, on relance à la fin
  rec.interimResults = false;
  rec.onresult = (e) => {
    const last = e.results[e.results.length - 1];
    if (last?.isFinal) void handleCommand(last[0].transcript);
  };
  rec.onerror = (e) => {
    if (e.error === "not-allowed" || e.error === "service-not-allowed") {
      active = false;
      onState(false);
      toast("Micro refusé — autorise le micro dans le navigateur.");
    }
    // "no-speech" / "network" : onend relancera si toujours actif
  };
  rec.onend = () => {
    if (active) {
      try { rec?.start(); } catch { /* déjà démarré */ }
    } else {
      onState(false);
    }
  };
  try { rec.start(); onState(true); } catch { onState(false); }
}

export function toggleVoice(onState: (listening: boolean) => void): void {
  if (active) {
    active = false;
    rec?.stop();
    stopSpeaking();
    onState(false);
    return;
  }
  active = true;
  startRecognition(onState);
  speak("Mode vocal activé. Dites : aide, pour les commandes.");
}

export function setVoiceContext(c: VoiceContext): void {
  ctx = c;
  stepIndex = -1;
}

/** À appeler quand de nouveaux résultats s'affichent : lecture auto si actif. */
export function announceResults(): void {
  if (active) readResults();
}
